import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    CalendarDays, Clock, Users, MessageSquare,
    Loader, UtensilsCrossed, Pencil, Plus, Minus
} from "lucide-react";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import PrimaryButton from "./components/PrimaryButton";
import useNav from "./hooks/useNav";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { toast } from "react-toastify";
import appConfig from "./config";
import { useAuthFetch } from "./hooks/useAuthFetch";

const STATUS_CONFIG = {
    pending:   { label: 'На подтверждении', cls: 'bg-yellow-100 text-yellow-700' },
    confirmed: { label: 'Подтверждено',     cls: 'bg-green-100 text-green-700' },
    canceled:  { label: 'Отменено',         cls: 'bg-red-100 text-red-600' },
    completed: { label: 'Завершено',        cls: 'bg-blue-100 text-blue-700' },
};

const SectionCard = ({ icon: Icon, title, action, children }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-brandBlue/10 flex items-center justify-center shrink-0">
                    <Icon size={14} className="text-brandBlue" />
                </div>
                <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
            </div>
            {action}
        </div>
        {children}
    </div>
);

const fieldClass = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-brandBlue focus:bg-white transition-colors disabled:opacity-50";

const ReservationEditPage = () => {
    const { reservationId } = useParams();
    const navigate = useNav();
    const { restaurant } = useRestaurant();
    const { authFetch } = useAuthFetch();

    const [reservation, setReservation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [peopleCount, setPeopleCount] = useState(2);
    const [comment, setComment] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const preorderEnabled = restaurant?.settings?.preorder_enabled ?? false;

    useEffect(() => {
        authFetch(`${appConfig.endpoint}/reservations/${reservationId}/view`)
            .then(data => {
                if (data.success) {
                    setReservation(data.reservation);
                    setPeopleCount(data.reservation.people_count);
                    setComment(data.reservation.comment || "");
                } else {
                    toast.error("Не удалось загрузить данные брони");
                }
            })
            .finally(() => setLoading(false));
    }, [reservationId]);

    const minutesUntil = reservation
        ? ((reservation.reserve_at
            ? reservation.reserve_at * 1000
            : new Date(`${reservation.date}T${reservation.time}`).getTime()
          ) - Date.now()) / 60000
        : 0;

    const canEditPreorder = preorderEnabled
        && reservation?.status !== 'canceled'
        && reservation?.status !== 'completed'
        && minutesUntil > 30;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const data = await authFetch(`${appConfig.endpoint}/reservations/${reservationId}/edit`, {
                method: "POST",
                body: JSON.stringify({ people_count: peopleCount, comment }),
            });
            if (data.success) {
                toast.success("Бронирование обновлено");
                navigate(-1);
            } else {
                toast.error(data.message || "Ошибка при обновлении");
            }
        } catch {
            toast.error("Произошла ошибка. Попробуйте снова.");
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader className="animate-spin text-brandBlue" size={32} />
            </div>
        );
    }

    if (!reservation) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <p className="text-gray-500 text-sm">Бронирование не найдено</p>
            </div>
        );
    }

    const statusCfg = STATUS_CONFIG[reservation.status]
        || { label: reservation.statusLabel || reservation.status, cls: 'bg-gray-100 text-gray-600' };
    const isActive = reservation.status !== 'canceled' && reservation.status !== 'completed';
    const dishes = Array.isArray(reservation.dishes) ? reservation.dishes : [];
    const dishesTotal = dishes.reduce((s, d) => s + (d.price || 0) * (d.quantity || 1), 0);

    const dateLabel = (() => {
        try {
            return new Date(reservation.date + 'T00:00:00').toLocaleDateString('ru-RU', {
                day: 'numeric', month: 'long', year: 'numeric'
            });
        } catch { return reservation.date; }
    })();

    return (
        <div className="min-h-screen bg-gray-50">
            <Header restaurantName={restaurant?.name} />

            <div className="max-w-screen-md mx-auto px-4 pb-32 pt-5 page-enter">

                {/* Title + status */}
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <BackButton onClick={() => navigate(-1)} disabled={isSaving} />
                        <h1 className="text-xl font-bold text-gray-900">Бронь #{reservation.id}</h1>
                    </div>
                    <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${statusCfg.cls}`}>
                        {statusCfg.label}
                    </span>
                </div>

                {/* Details */}
                <SectionCard icon={CalendarDays} title="Детали бронирования">
                    <div className="space-y-0">
                        {[
                            { label: 'Дата', value: dateLabel },
                            { label: 'Время', value: reservation.time },
                            {
                                label: 'Гостей',
                                value: `${reservation.people_count} ${reservation.people_count === 1 ? 'персона' : reservation.people_count < 5 ? 'персоны' : 'персон'}`
                            },
                            ...(reservation.comment ? [{ label: 'Пожелание', value: reservation.comment }] : []),
                            { label: 'Создано', value: reservation.created_at },
                        ].map(({ label, value }) => (
                            <div key={label} className="flex justify-between items-start py-2.5 border-b border-gray-50 last:border-0">
                                <span className="text-sm text-gray-400">{label}</span>
                                <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{value}</span>
                            </div>
                        ))}
                    </div>
                </SectionCard>

                {/* Pre-order section */}
                {preorderEnabled && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                                    <UtensilsCrossed size={14} className="text-orange-500" />
                                </div>
                                <h2 className="text-sm font-semibold text-gray-800">Предзаказ блюд</h2>
                                {dishes.length > 0 && (
                                    <span className="text-xs bg-orange-50 text-orange-600 font-semibold px-2 py-0.5 rounded-full">
                                        {dishes.length} поз.
                                    </span>
                                )}
                            </div>
                            {canEditPreorder && (
                                <button
                                    onClick={() => navigate('/reservation-preorder', {
                                        state: {
                                            reservationId: reservation.id,
                                            date: reservation.date,
                                            time: reservation.time,
                                            existingDishes: dishes,
                                        }
                                    })}
                                    className="flex items-center gap-1 text-xs text-brandBlue font-semibold"
                                >
                                    {dishes.length > 0 ? <><Pencil size={12} /> Изменить</> : <><Plus size={12} /> Добавить</>}
                                </button>
                            )}
                        </div>

                        {dishes.length === 0 ? (
                            <div className="py-3 text-center">
                                <p className="text-sm text-gray-400">
                                    {canEditPreorder
                                        ? 'Блюда не выбраны — нажмите «Добавить»'
                                        : minutesUntil <= 30 && minutesUntil > 0
                                            ? 'Изменить предзаказ уже нельзя — менее 30 минут до брони'
                                            : 'Предзаказ не оформлен'}
                                </p>
                            </div>
                        ) : (
                            <div>
                                {dishes.map((dish, i) => (
                                    <div key={i} className="flex justify-between items-center py-2.5 border-b border-gray-50 last:border-0">
                                        <span className="text-sm text-gray-800">
                                            {dish.title || dish.name}
                                            {dish.quantity > 1 && (
                                                <span className="text-gray-400"> ×{dish.quantity}</span>
                                            )}
                                        </span>
                                        <span className="text-sm font-medium text-gray-700">
                                            {((dish.price || 0) * (dish.quantity || 1)).toLocaleString('ru-RU')} ₽
                                        </span>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-3">
                                    <span className="text-sm font-semibold text-gray-700">Итого</span>
                                    <span className="text-sm font-bold text-gray-900">
                                        {dishesTotal.toLocaleString('ru-RU')} ₽
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Edit form */}
                {isActive && (
                    <>
                        <SectionCard icon={Users} title="Количество гостей">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Сколько вас будет?</span>
                                <div className="flex items-center gap-3 bg-gray-50 rounded-2xl border border-gray-200 p-1">
                                    <button
                                        onClick={() => setPeopleCount(p => Math.max(1, p - 1))}
                                        disabled={isSaving || peopleCount <= 1}
                                        className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-500 hover:bg-white hover:shadow-sm transition-all disabled:opacity-30"
                                    >
                                        <Minus size={14} strokeWidth={2.5} />
                                    </button>
                                    <span className="text-base font-bold text-gray-900 min-w-[28px] text-center">
                                        {peopleCount}
                                    </span>
                                    <button
                                        onClick={() => setPeopleCount(p => Math.min(20, p + 1))}
                                        disabled={isSaving || peopleCount >= 20}
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors disabled:opacity-30"
                                    >
                                        <Plus size={14} strokeWidth={2.5} />
                                    </button>
                                </div>
                            </div>
                        </SectionCard>

                        <SectionCard icon={MessageSquare} title="Пожелания">
                            <textarea
                                rows={3}
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                                disabled={isSaving}
                                placeholder="Например: столик у окна, детский стульчик…"
                                className={`${fieldClass} resize-none`}
                            />
                        </SectionCard>
                    </>
                )}
            </div>

            {isActive && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg z-10">
                    <div className="max-w-screen-md mx-auto">
                        <PrimaryButton
                            onClick={handleSave}
                            disabled={isSaving}
                            className="w-full flex items-center justify-center gap-2 py-3.5"
                        >
                            {isSaving ? <Loader className="animate-spin" size={18} /> : "Сохранить изменения"}
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationEditPage;